import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Users2, UserPlus, Star, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { boardData } from '../data/board';

const BoardOfDirectors = () => {
  const navigate = useNavigate();
  // Separate officers and regular members
  const officers = boardData.members.filter(m => m.isOfficer);
  const regularMembers = boardData.members.filter(m => !m.isOfficer && !m.isVacant);
  const vacantPositions = boardData.members.filter(m => m.isVacant);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      {/* Hero Section */}
      <section id="main-content" role="main" tabIndex={-1} className="pt-32 pb-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
        {/* Background Decoration */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 left-20 w-40 h-40 border border-white/10 rounded-full" />
          <div className="absolute bottom-20 right-20 w-60 h-60 border border-white/10 rounded-full" />
          <div className="absolute top-1/2 right-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Back Link */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-teal-300 hover:text-teal-200 mb-8 transition-colors duration-300"
            data-testid="back-home-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center">
              <Users2 className="w-8 h-8 text-white" />
            </div>
            <span className="px-4 py-2 bg-teal-500/20 text-teal-300 rounded-full text-sm font-semibold">
              Leadership
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6" data-testid="board-page-title">
            {boardData.title}
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl leading-relaxed">
            {boardData.subtitle}
          </p>
        </div>
      </section>

      {/* Board Description */}
      <section className="py-12 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-lg text-slate-600 text-center max-w-4xl mx-auto">
            {boardData.description}
          </p>
        </div>
      </section>

      {/* Board Officers Section */}
      <section className="py-16 bg-gradient-to-br from-slate-100 to-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-3 mb-10">
            <Star className="w-6 h-6 text-amber-500" />
            <h2 className="text-2xl font-bold text-slate-800">Board Officers</h2>
            <Star className="w-6 h-6 text-amber-500" />
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {officers.map((member) => (
              <div 
                key={member.id}
                className="bg-white rounded-3xl overflow-hidden shadow-lg border-2 border-amber-200 hover:shadow-xl hover:border-amber-300 transition-all duration-300 group"
                data-testid={`board-officer-card-${member.id}`}
              >
                {/* Officer Photo */}
                <div className="aspect-square overflow-hidden bg-slate-100">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                
                {/* Officer Info */}
                <div className="p-5">
                  <h3 className="text-lg font-bold text-slate-800 mb-1">
                    {member.name}
                  </h3>
                  <p className="text-amber-600 font-semibold mb-1">
                    {member.title}
                  </p>
                  {member.affiliation && (
                    <p className="text-slate-500 text-sm italic mb-2">
                      {member.affiliation}
                    </p>
                  )}
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {member.bio}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Board Members Grid */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-slate-800 text-center mb-10">Board Members</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {regularMembers.map((member) => (
              <div 
                key={member.id}
                className="bg-white rounded-3xl overflow-hidden shadow-lg border border-slate-100 hover:shadow-xl transition-all duration-300 group"
                data-testid={`board-member-card-${member.id}`}
              >
                {/* Member Photo */}
                <div className="aspect-square overflow-hidden bg-slate-100">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                
                {/* Member Info */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-slate-800 mb-1">
                    {member.name}
                  </h3>
                  <p className="text-teal-600 font-medium mb-1">
                    {member.title}
                  </p>
                  {member.affiliation && (
                    <p className="text-slate-500 text-sm italic mb-3">
                      {member.affiliation}
                    </p>
                  )}
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {member.bio}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vacant Positions Section */}
      <section className="py-16 bg-white border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-slate-800 text-center mb-4">Open Positions</h2>
          <p className="text-slate-500 text-center mb-10 max-w-2xl mx-auto">
            We are seeking dedicated individuals to join our board. If you're passionate about early childhood education, consider serving on our board.
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {vacantPositions.map((position) => (
              <div 
                key={position.id}
                className="bg-slate-50 rounded-3xl overflow-hidden border-2 border-dashed border-slate-300 hover:border-teal-400 transition-all duration-300 group"
                data-testid={`vacant-position-${position.id}`}
              >
                {/* Placeholder */}
                <div className="aspect-square overflow-hidden bg-slate-100 flex items-center justify-center">
                  <div className="text-center">
                    <UserPlus className="w-16 h-16 text-slate-300 mx-auto mb-3 group-hover:text-teal-400 transition-colors duration-300" />
                    <p className="text-slate-400 font-medium">Position Available</p>
                  </div>
                </div>
                
                {/* Position Info */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-slate-500 mb-1">
                    {position.name}
                  </h3>
                  <p className="text-slate-400 font-medium mb-3">
                    {position.title}
                  </p>
                  <p className="text-slate-500 text-sm leading-relaxed">
                    {position.bio}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Board Interest CTA */}
          <div className="mt-12 text-center">
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-8 sm:p-10 max-w-2xl mx-auto shadow-xl">
              <h3 className="text-2xl font-bold text-white mb-3">Interested in Serving?</h3>
              <p className="text-slate-300 mb-6 leading-relaxed">
                We welcome individuals who are passionate about early childhood education and committed to making a difference in Washington, DC. Share your interest and let us learn more about you.
              </p>
              <Button
                onClick={() => navigate('/board-interest')}
                size="lg"
                className="bg-[#2e7d5e] hover:bg-[#3d9e78] text-white px-8 py-6 text-base font-semibold shadow-lg group"
                data-testid="board-interest-cta-btn"
              >
                Submit Board Interest Form
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BoardOfDirectors;
